export type GradeGroup = 'preschool' | 'primary' | 'middle' | 'secondary' | 'senior' | 'vocational';
export type SubjectCategory = 'language' | 'core' | 'social' | 'activity' | 'skill' | 'sports' | 'elective';

export interface CatalogEntry {
  name: string;
  category: SubjectCategory;
  gradeGroups: GradeGroup[];
}

export const GRADE_GROUP_LABELS: Record<GradeGroup, string> = {
  preschool: 'Preschool',
  primary: 'Primary (1–5)',
  middle: 'Middle (6–8)',
  secondary: 'Secondary (9–10)',
  senior: 'Senior (11–12)',
  vocational: 'Vocational',
};

export const CATEGORY_LABELS: Record<SubjectCategory, string> = {
  language: 'Language',
  core: 'Core',
  social: 'Social Science',
  activity: 'Arts & Activities',
  skill: 'Skills & Tech',
  sports: 'Sports & Health',
  elective: 'Elective',
};

export const CATEGORY_CAT_CLASS: Record<SubjectCategory, string> = {
  language: 'cat-blue',
  core: 'cat-purple',
  social: 'cat-amber',
  activity: 'cat-rose',
  skill: 'cat-teal',
  sports: 'cat-green',
  elective: 'cat-indigo',
};

export const SUBJECT_CATALOG: CatalogEntry[] = [
  // ── Preschool: Playgroup ──────────────────────────────────────────────────────
  { name: 'Pre-Literacy',           category: 'core',     gradeGroups: ['preschool'] },
  { name: 'Pre-Numeracy',           category: 'core',     gradeGroups: ['preschool'] },
  { name: 'Language Development',   category: 'language', gradeGroups: ['preschool'] },
  { name: 'Rhymes & Songs',         category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Rhymes',                 category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Sensory Activities',     category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Fine Motor Skills',      category: 'sports',   gradeGroups: ['preschool'] },
  { name: 'Gross Motor Skills',     category: 'sports',   gradeGroups: ['preschool'] },
  { name: 'Motor Skills',           category: 'sports',   gradeGroups: ['preschool'] },
  { name: 'Creative Play',          category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Emotional Development',  category: 'skill',    gradeGroups: ['preschool'] },
  { name: 'Circle Time',            category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Theme-Based Learning',   category: 'core',     gradeGroups: ['preschool'] },
  { name: 'Free Play',              category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Environmental Awareness',category: 'core',     gradeGroups: ['preschool'] },
  { name: 'Music & Movement',       category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Physical Development',   category: 'sports',   gradeGroups: ['preschool'] },

  // ── Preschool: Nursery ────────────────────────────────────────────────────────
  { name: 'English Readiness',      category: 'language', gradeGroups: ['preschool'] },
  { name: 'Hindi Readiness',        category: 'language', gradeGroups: ['preschool'] },
  { name: 'Pre-Mathematics',        category: 'core',     gradeGroups: ['preschool'] },
  { name: 'General Awareness',      category: 'core',     gradeGroups: ['preschool'] },
  { name: 'Drawing & Coloring',     category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Activity Time',          category: 'activity', gradeGroups: ['preschool'] },
  { name: 'Creative Expression',    category: 'activity', gradeGroups: ['preschool'] },

  // ── Preschool: LKG / UKG ─────────────────────────────────────────────────────
  { name: 'Reading Skills',         category: 'language', gradeGroups: ['preschool'] },
  { name: 'Writing Skills',         category: 'language', gradeGroups: ['preschool'] },
  { name: 'Computer Awareness',     category: 'skill',    gradeGroups: ['preschool'] },
  { name: 'Activity Based Learning',category: 'skill',    gradeGroups: ['preschool', 'primary'] },

  // ── Shared: Preschool → beyond ────────────────────────────────────────────────
  { name: 'English',                category: 'language', gradeGroups: ['preschool', 'primary', 'middle', 'secondary', 'senior'] },
  { name: 'Hindi',                  category: 'language', gradeGroups: ['preschool', 'primary', 'middle', 'secondary', 'senior'] },
  { name: 'Mathematics',            category: 'core',     gradeGroups: ['preschool', 'primary', 'middle', 'secondary', 'senior'] },
  { name: 'Environmental Studies',  category: 'core',     gradeGroups: ['preschool', 'primary', 'middle'] },
  { name: 'General Knowledge',      category: 'core',     gradeGroups: ['preschool', 'primary', 'middle'] },
  { name: 'Storytelling',           category: 'activity', gradeGroups: ['preschool', 'primary'] },
  { name: 'Art & Craft',            category: 'activity', gradeGroups: ['preschool', 'primary'] },
  { name: 'Drawing',                category: 'activity', gradeGroups: ['preschool', 'primary'] },
  { name: 'Coloring',               category: 'activity', gradeGroups: ['preschool', 'primary'] },
  { name: 'Music',                  category: 'activity', gradeGroups: ['preschool', 'primary', 'middle'] },
  { name: 'Dance',                  category: 'activity', gradeGroups: ['preschool', 'primary', 'middle'] },
  { name: 'Physical Education',     category: 'sports',   gradeGroups: ['preschool', 'primary', 'middle', 'secondary'] },
  { name: 'Life Skills',            category: 'skill',    gradeGroups: ['preschool', 'primary', 'middle'] },
  { name: 'Social Skills',          category: 'skill',    gradeGroups: ['preschool', 'primary'] },
  { name: 'Phonics',                category: 'language', gradeGroups: ['preschool', 'primary'] },
  { name: 'Conversation Skills',    category: 'language', gradeGroups: ['preschool', 'primary'] },
  { name: 'Communication Skills',   category: 'language', gradeGroups: ['preschool', 'primary', 'middle'] },
  { name: 'Value Education',        category: 'skill',    gradeGroups: ['preschool', 'primary', 'middle'] },

  // ── Primary (1–5): Languages ──────────────────────────────────────────────────
  { name: 'Telugu',                 category: 'language', gradeGroups: ['primary', 'middle'] },
  { name: 'Tamil',                  category: 'language', gradeGroups: ['primary', 'middle'] },
  { name: 'Kannada',                category: 'language', gradeGroups: ['primary', 'middle'] },
  { name: 'Malayalam',              category: 'language', gradeGroups: ['primary', 'middle'] },
  { name: 'Marathi',                category: 'language', gradeGroups: ['primary', 'middle'] },
  { name: 'Gujarati',               category: 'language', gradeGroups: ['primary', 'middle'] },
  { name: 'Bengali',                category: 'language', gradeGroups: ['primary', 'middle'] },
  { name: 'Punjabi',                category: 'language', gradeGroups: ['primary', 'middle'] },
  { name: 'Urdu',                   category: 'language', gradeGroups: ['primary', 'middle', 'secondary'] },
  { name: 'Sanskrit',               category: 'language', gradeGroups: ['primary', 'middle', 'secondary'] },
  { name: 'French',                 category: 'language', gradeGroups: ['primary', 'middle', 'secondary', 'senior'] },
  { name: 'German',                 category: 'language', gradeGroups: ['primary', 'middle', 'secondary', 'senior'] },
  { name: 'Spanish',                category: 'language', gradeGroups: ['primary', 'middle', 'secondary', 'senior'] },

  // ── Primary (1–5): Core & Co-Scholastic ──────────────────────────────────────
  { name: 'Science',                category: 'core',     gradeGroups: ['primary', 'middle'] },
  { name: 'Social Studies',         category: 'social',   gradeGroups: ['primary'] },
  { name: 'Computer Science',       category: 'skill',    gradeGroups: ['primary', 'middle', 'secondary', 'senior'] },
  { name: 'Information Technology', category: 'skill',    gradeGroups: ['primary', 'middle', 'secondary'] },
  { name: 'Art Education',          category: 'activity', gradeGroups: ['primary', 'middle'] },
  { name: 'Craft',                  category: 'activity', gradeGroups: ['primary'] },
  { name: 'Yoga',                   category: 'sports',   gradeGroups: ['primary', 'middle'] },
  { name: 'Moral Science',          category: 'skill',    gradeGroups: ['primary', 'middle'] },
  { name: 'Library',                category: 'elective', gradeGroups: ['primary', 'middle'] },
  { name: 'Health Education',       category: 'sports',   gradeGroups: ['primary', 'middle'] },

  // ── Middle (6–8): Additional ──────────────────────────────────────────────────
  { name: 'Regional Language',      category: 'language', gradeGroups: ['middle'] },
  { name: 'Physics',                category: 'core',     gradeGroups: ['middle', 'secondary', 'senior'] },
  { name: 'Chemistry',              category: 'core',     gradeGroups: ['middle', 'secondary', 'senior'] },
  { name: 'Biology',                category: 'core',     gradeGroups: ['middle', 'secondary', 'senior'] },
  { name: 'Social Science',         category: 'social',   gradeGroups: ['middle', 'secondary'] },
  { name: 'History',                category: 'social',   gradeGroups: ['middle', 'secondary', 'senior'] },
  { name: 'Geography',              category: 'social',   gradeGroups: ['middle', 'secondary', 'senior'] },
  { name: 'Civics',                 category: 'social',   gradeGroups: ['middle', 'secondary'] },
  { name: 'Economics',              category: 'social',   gradeGroups: ['middle', 'secondary', 'senior'] },
  { name: 'Artificial Intelligence',category: 'skill',    gradeGroups: ['middle', 'secondary', 'senior', 'vocational'] },
  { name: 'Robotics',               category: 'skill',    gradeGroups: ['middle', 'secondary', 'vocational'] },
  { name: 'Environmental Education',category: 'core',     gradeGroups: ['middle'] },

  // ── Secondary (9–10): CBSE / ICSE ────────────────────────────────────────────
  { name: 'English Language & Literature', category: 'language', gradeGroups: ['secondary'] },
  { name: 'English Communicative',  category: 'language', gradeGroups: ['secondary'] },
  { name: 'Hindi A',                category: 'language', gradeGroups: ['secondary'] },
  { name: 'Hindi B',                category: 'language', gradeGroups: ['secondary'] },
  { name: 'Mathematics Standard',   category: 'core',     gradeGroups: ['secondary'] },
  { name: 'Mathematics Basic',      category: 'core',     gradeGroups: ['secondary'] },
  { name: 'Computer Applications',  category: 'skill',    gradeGroups: ['secondary'] },
  { name: 'Data Science',           category: 'skill',    gradeGroups: ['secondary', 'senior', 'vocational'] },
  { name: 'Multimedia',             category: 'skill',    gradeGroups: ['secondary'] },
  { name: 'Financial Markets Management', category: 'skill', gradeGroups: ['secondary'] },
  { name: 'Retail',                 category: 'skill',    gradeGroups: ['secondary', 'vocational'] },
  { name: 'Healthcare',             category: 'skill',    gradeGroups: ['secondary', 'vocational'] },
  { name: 'Tourism',                category: 'skill',    gradeGroups: ['secondary', 'vocational'] },
  { name: 'Banking',                category: 'skill',    gradeGroups: ['secondary'] },
  { name: 'Agriculture',            category: 'skill',    gradeGroups: ['secondary', 'vocational'] },
  { name: 'Home Science',           category: 'elective', gradeGroups: ['secondary'] },
  { name: 'Painting',               category: 'activity', gradeGroups: ['secondary', 'senior'] },
  { name: 'Commercial Studies',     category: 'social',   gradeGroups: ['secondary'] },
  { name: 'Environmental Science',  category: 'core',     gradeGroups: ['secondary', 'senior'] },

  // ── Senior (11–12): Science ───────────────────────────────────────────────────
  { name: 'Biotechnology',          category: 'core',     gradeGroups: ['senior'] },
  { name: 'Engineering Graphics',   category: 'skill',    gradeGroups: ['senior'] },
  { name: 'Psychology',             category: 'social',   gradeGroups: ['senior'] },
  { name: 'Informatics Practices',  category: 'skill',    gradeGroups: ['senior'] },

  // ── Senior (11–12): Commerce ──────────────────────────────────────────────────
  { name: 'Accountancy',            category: 'core',     gradeGroups: ['senior'] },
  { name: 'Business Studies',       category: 'core',     gradeGroups: ['senior'] },
  { name: 'Applied Mathematics',    category: 'core',     gradeGroups: ['senior'] },
  { name: 'Entrepreneurship',       category: 'skill',    gradeGroups: ['senior', 'vocational'] },

  // ── Senior (11–12): Humanities ────────────────────────────────────────────────
  { name: 'Political Science',      category: 'social',   gradeGroups: ['senior'] },
  { name: 'Sociology',              category: 'social',   gradeGroups: ['senior'] },
  { name: 'Philosophy',             category: 'elective', gradeGroups: ['senior'] },
  { name: 'Legal Studies',          category: 'elective', gradeGroups: ['senior'] },
  { name: 'Fine Arts',              category: 'activity', gradeGroups: ['senior'] },
  { name: 'Mass Media Studies',     category: 'elective', gradeGroups: ['senior'] },

  // ── Vocational / Modern ───────────────────────────────────────────────────────
  { name: 'Machine Learning',       category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Coding',                 category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Programming',            category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Python',                 category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Web Development',        category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Cyber Security',         category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Financial Literacy',     category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Digital Marketing',      category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Design Thinking',        category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Graphic Design',         category: 'activity', gradeGroups: ['vocational'] },
  { name: 'Animation',              category: 'activity', gradeGroups: ['vocational'] },
  { name: 'Photography',            category: 'activity', gradeGroups: ['vocational'] },
  { name: 'Film Studies',           category: 'activity', gradeGroups: ['vocational'] },
  { name: 'Journalism',             category: 'elective', gradeGroups: ['vocational'] },
  { name: 'Media Studies',          category: 'elective', gradeGroups: ['vocational'] },
  { name: 'Food Production',        category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Hotel Management',       category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Retail Management',      category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Travel & Tourism',       category: 'skill',    gradeGroups: ['vocational'] },
  { name: 'Fashion Studies',        category: 'activity', gradeGroups: ['vocational'] },
];
